/** Follow one batch background task to its result, for dialogs that review a
 *  dry run inline (generic batch edit, booking batch edit).
 *
 *  Polls the task every 2 s while it is active, then fetches its result — which
 *  lands in the archive moments AFTER the task completes, so a 404 is retried
 *  through that gap rather than surfaced. Keyed by task id: passing `null`
 *  (e.g. after the operation changed) drops back to "no task".
 */
import { useQuery } from '@tanstack/react-query';

import * as bgtasksApi from '@/api/bgtasks';
import { TASK_STATE, isActiveState } from '@/api/bgtasks';
import { ConsoleApiError } from '@/api/client';

export function useBatchTaskResult<R>(taskId: string | null, key: string) {
  const taskQuery = useQuery({
    queryKey: [`${key}-task`, taskId],
    queryFn: () => bgtasksApi.retrieve(taskId as string),
    enabled: taskId != null,
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      return state == null || isActiveState(state) ? 2000 : false;
    },
  });
  const taskState = taskQuery.data?.state ?? null;
  const finished = taskState != null && !isActiveState(taskState);
  const succeeded = taskState === TASK_STATE.COMPLETED;

  const resultQuery = useQuery({
    queryKey: [`${key}-result`, taskId],
    queryFn: () => bgtasksApi.result(taskId as string),
    enabled: taskId != null && succeeded,
    retry: (failureCount, error) =>
      failureCount < 10 && error instanceof ConsoleApiError && error.status === 404,
    retryDelay: 1200,
    staleTime: Infinity,
  });

  const result = (resultQuery.data?.result as unknown as R | undefined) ?? null;
  return {
    task: taskQuery.data ?? null,
    progressPct: taskQuery.data?.progress?.pct ?? null,
    finished,
    succeeded,
    result,
    /** Task dispatched and still running. */
    waitingForTask: taskId != null && result == null && !finished,
    /** Task completed; its archived result is still being fetched. */
    waitingForResult: succeeded && result == null && !resultQuery.isError,
    resultError: resultQuery.isError,
  };
}
